//src/pages/public/PublicSurveyPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Link2Off,
  Loader2,
  LockKeyhole,
  RefreshCcw,
  Send,
  ShieldAlert,
  ShieldOff,
  Sparkles,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePublicSurvey, useSubmitPublicSurvey } from "@/hooks/usePublicSurvey";
import type {
  PublicSurveyQuestion,
  PublicSurveySubmitAnswer,
} from "@/types/publicSurvey";

type AnswerValue =
  | { kind: "option"; selected_option_id: string }
  | { kind: "text"; answer_text: string }
  | { kind: "numeric"; answer_numeric: string }
  | { kind: "multiple"; selected_options: string[] };

type AnswersMap = Record<string, AnswerValue>;

type PublicSurveyUnavailableVariant =
  | "invalid"
  | "unavailable"
  | "not-open-yet"
  | "closed"
  | "access-disabled"
  | "already-answered"
  | "generic";

const CHOICE_TYPES = new Set([
  "single_choice",
  "likert",
  "scale",
  "yes_no",
]);

const MULTIPLE_CHOICE_TYPES = new Set(["multiple_choice"]);
const SYSTEM_SELECT_TYPES = new Set(["system_select"]);

const TEXT_TYPES = new Set(["text", "textarea", "long_text"]);
const NUMERIC_TYPES = new Set(["number", "numeric", "integer", "decimal"]);

const NORMEX_WEBSITE = "https://www.normex.com.br";
const TEXT_ANSWER_MAX_LENGTH = 1000;

function getQuestionKind(questionType: string) {
  if (CHOICE_TYPES.has(questionType)) return "choice";
  if (MULTIPLE_CHOICE_TYPES.has(questionType)) return "multiple";
  if (SYSTEM_SELECT_TYPES.has(questionType)) return "system_select";
  if (TEXT_TYPES.has(questionType)) return "text";
  if (NUMERIC_TYPES.has(questionType)) return "numeric";
  return "choice";
}

function buildGroupedQuestions(questions: PublicSurveyQuestion[], sections: any[]) {
  if (!sections.length) {
    return [
      {
        id: "no-section",
        title: "Perguntas",
        description: null,
        sort_order: 0,
        questions,
      },
    ];
  }

  const bySection = sections.map((section) => ({
    ...section,
    questions: questions.filter((q) => q.section_id === section.id),
  }));

  const noSectionQuestions = questions.filter((q) => !q.section_id);
  if (noSectionQuestions.length) {
    bySection.unshift({
      id: "no-section",
      title: "Perguntas gerais",
      description: null,
      sort_order: -1,
      questions: noSectionQuestions,
    });
  }

  return bySection.filter((section) => section.questions.length > 0);
}

function questionIsAnswered(question: PublicSurveyQuestion, value?: AnswerValue) {
  if (!value) return false;

  const kind = getQuestionKind(question.question_type);

  if (kind === "choice") {
    return value.kind === "option" && !!value.selected_option_id;
  }

  if (kind === "multiple") {
    const count = value.kind === "multiple" ? value.selected_options.length : 0;
    return question.is_required ? count >= Number(question.min_selected_options || 1) : count > 0;
  }

  if (kind === "system_select") {
    return value.kind === "text" && value.answer_text.trim().length > 0;
  }

  if (kind === "text") {
    return value.kind === "text" && value.answer_text.trim().length > 0;
  }

  if (kind === "numeric") {
    return value.kind === "numeric" && value.answer_numeric.trim().length > 0;
  }

  return false;
}

function buildSubmitAnswers(
  questions: PublicSurveyQuestion[],
  answers: AnswersMap,
): PublicSurveySubmitAnswer[] {
  return questions.flatMap((question) => {
    const answer = answers[question.id];
    if (!answer) return [];

    if (answer.kind === "option" && answer.selected_option_id) {
      return [
        {
          question_id: question.id,
          selected_option_id: answer.selected_option_id,
        },
      ];
    }

    if (answer.kind === "text" && answer.answer_text.trim()) {
      return [
        {
          question_id: question.id,
          answer_text: answer.answer_text.trim(),
        },
      ];
    }

    if (answer.kind === "multiple" && answer.selected_options.length > 0) {
      return [
        {
          question_id: question.id,
          selected_options: answer.selected_options,
        },
      ];
    }

    if (answer.kind === "numeric" && answer.answer_numeric.trim()) {
      if (!/^-?\d+$/.test(answer.answer_numeric.trim())) return [];
      const parsed = Number(answer.answer_numeric.trim());
      if (Number.isNaN(parsed)) return [];
      return [
        {
          question_id: question.id,
          answer_numeric: parsed,
        },
      ];
    }

    return [];
  });
}

function formatDateRange(startsAt?: string | null, endsAt?: string | null) {
  const formatter = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const start = startsAt ? formatter.format(new Date(startsAt)) : null;
  const end = endsAt ? formatter.format(new Date(endsAt)) : null;

  if (start && end) return `${start} até ${end}`;
  if (start) return `A partir de ${start}`;
  if (end) return `Até ${end}`;
  return "Disponível por tempo indeterminado";
}

function getSectionProgress(
  section: { questions: PublicSurveyQuestion[] },
  answers: AnswersMap,
) {
  const total = section.questions.length;
  const answered = section.questions.filter((q) => questionIsAnswered(q, answers[q.id])).length;
  return { total, answered };
}

function getPublicSurveyErrorDetail(error: any) {
  return (
    error?.response?.data?.detail ||
    error?.data?.detail ||
    error?.message ||
    "O link pode estar inválido, expirado ou a pesquisa já foi respondida."
  );
}

function getPublicSurveyUnavailableVariant(detail?: string | null): PublicSurveyUnavailableVariant {
  const message = (detail || "").toLowerCase();

  if (message.includes("token de pesquisa não encontrado")) return "invalid";
  if (message.includes("a campanha ainda não está aberta")) return "not-open-yet";
  if (message.includes("a campanha já foi encerrada")) return "closed";
  if (message.includes("seu acesso a esta pesquisa foi desativado")) return "access-disabled";
  if (
    message.includes("este link não está disponível para nova resposta") ||
    message.includes("já existe uma resposta registrada para este token") ||
    message.includes("este token já foi utilizado ou está indisponível")
  ) {
    return "already-answered";
  }
  if (message.includes("limite de respostas previsto para esta campanha foi atingido")) return "unavailable";
  if (message.includes("a campanha não está disponível para resposta")) return "unavailable";

  return "generic";
}

function SurveySkeleton() {
  return (
    <div className="min-h-screen bg-neutral-950 px-4 py-10 text-neutral-50">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-4xl items-center justify-center">
        <Card className="w-full overflow-hidden rounded-[32px] border-white/10 bg-white/[0.04] p-10 shadow-2xl shadow-black/30">
          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-white/5 ring-1 ring-white/10">
              <Loader2 className="h-7 w-7 animate-spin text-neutral-200" />
            </div>

            <div className="mt-6 inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs uppercase tracking-[0.25em] text-neutral-400">
              Validando acesso
            </div>

            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-white">
              Preparando sua pesquisa
            </h1>

            <p className="mt-4 max-w-xl text-sm leading-7 text-neutral-300">
              Estamos verificando seu link e carregando o questionário para uma
              experiência mais fluida, segura e sem interrupções.
            </p>

            <div className="mt-8 grid w-full gap-3 sm:grid-cols-3">
              <div className="h-24 rounded-[24px] bg-white/5 animate-pulse" />
              <div className="h-24 rounded-[24px] bg-white/5 animate-pulse" />
              <div className="h-24 rounded-[24px] bg-white/5 animate-pulse" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function SurveyFinishedState({ tenantName }: { tenantName?: string | null }) {
  return (
    <div className="min-h-screen bg-neutral-950 px-4 py-10 text-neutral-50">
      <div className="mx-auto max-w-3xl">
        <Card className="rounded-[28px] border-white/10 bg-white/[0.03] p-8 shadow-2xl shadow-black/30">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-400">
            <CheckCircle2 className="h-7 w-7" />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Respostas enviadas com sucesso
            </h1>

            <p className="max-w-2xl text-base leading-7 text-neutral-300">
              Sua participação ajuda a identificar fatores psicossociais e melhorar
              o ambiente de trabalho com mais segurança e responsabilidade.
            </p>

            {tenantName ? (
              <p className="text-xs text-neutral-500">Empresa: {tenantName}</p>
            ) : null}
          </div>

          <div className="mt-6">
            <a href={NORMEX_WEBSITE} target="_blank" rel="noreferrer">
              <Button className="rounded-2xl">
                Ir para o site da Normex
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </Card>
      </div>
    </div>
  );
}

function SurveyUnavailableState({
  variant,
  detail,
}: {
  variant: PublicSurveyUnavailableVariant;
  detail?: string | null;
}) {
  const content = {
    invalid: {
      icon: Link2Off,
      badge: "Link inválido",
      title: "Este link não é válido",
      description:
        "O endereço acessado não corresponde a uma pesquisa ativa. Verifique se o link foi copiado corretamente ou solicite um novo acesso.",
    },
    unavailable: {
      icon: ShieldAlert,
      badge: "Pesquisa indisponível",
      title: "Esta pesquisa não está disponível no momento",
      description:
        "A campanha vinculada a este link não está aberta para respostas agora. Isso pode acontecer quando a coleta foi encerrada ou retirada de circulação.",
    },
    "not-open-yet": {
      icon: Clock3,
      badge: "Ainda não começou",
      title: "A pesquisa ainda não foi liberada",
      description:
        "A coleta ainda não começou. Tente acessar novamente no período informado pelo organizador da campanha.",
    },
    closed: {
      icon: Clock3,
      badge: "Prazo encerrado",
      title: "O prazo da pesquisa terminou",
      description:
        "Esta pesquisa não está mais aceitando respostas. O período de participação foi encerrado pelo organizador.",
    },
    "access-disabled": {
      icon: ShieldOff,
      badge: "Acesso desativado",
      title: "Seu acesso foi desativado",
      description:
        "Este link deixou de estar habilitado para resposta. Se isso parecer incorreto, entre em contato com o responsável pela campanha.",
    },
    "already-answered": {
      icon: CheckCircle2,
      badge: "Resposta já registrada",
      title: "Sua participação já foi concluída",
      description:
        "Este link já foi utilizado anteriormente. Para preservar a integridade da pesquisa, cada acesso permite apenas uma resposta.",
    },
    generic: {
      icon: ShieldAlert,
      badge: "Não foi possível abrir",
      title: "Não foi possível abrir a pesquisa",
      description:
        "Ocorreu um problema ao carregar este link. Atualize a página ou tente novamente em alguns instantes.",
    },
  }[variant];

  const Icon = content.icon;

  return (
    <div className="min-h-screen bg-neutral-950 px-4 py-10 text-neutral-50">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
        <Card className="relative w-full overflow-hidden rounded-[32px] border-white/10 bg-white/[0.04] p-0 shadow-2xl shadow-black/30">
          <div className="absolute inset-x-0 top-0 h-32 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.12),_transparent_55%)]" />

          <div className="relative grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="p-8 sm:p-10 lg:p-12">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium tracking-[0.18em] text-neutral-300 uppercase">
                {content.badge}
              </div>

              <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-[22px] bg-white/5 ring-1 ring-white/10">
                <Icon className="h-8 w-8 text-white" />
              </div>

              <h1 className="mt-6 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                {content.title}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-neutral-300 sm:text-base">
                {content.description}
              </p>

              {detail ? (
                <div className="mt-6 rounded-[22px] border border-white/10 bg-black/20 px-4 py-3 text-sm leading-6 text-neutral-300">
                  <span className="font-medium text-neutral-100">Detalhe:</span> {detail}
                </div>
              ) : null}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  className="rounded-2xl"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Tentar novamente
                </Button>

                <a href={NORMEX_WEBSITE} target="_blank" rel="noreferrer">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-2xl border-white/15 bg-white/5 text-neutral-100 hover:bg-white/10 hover:text-white sm:w-auto"
                  >
                    Ir para o site da Normex
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>

            <div className="border-t border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-8 sm:p-10 lg:border-l lg:border-t-0 lg:p-12">
              <div className="space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">
                    O que aconteceu
                  </div>
                  <p className="mt-3 text-sm leading-7 text-neutral-300">
                    Esta tela substitui a mensagem genérica de erro por um estado mais
                    claro, com contexto visual e próximos passos para o usuário.
                  </p>
                </div>

                <div className="rounded-[24px] border border-white/10 bg-black/20 p-5">
                  <div className="text-sm font-medium text-neutral-100">
                    Próximas ações recomendadas
                  </div>
                  <ul className="mt-3 space-y-2 text-sm leading-6 text-neutral-300">
                    <li>• confira se o link foi copiado completo</li>
                    <li>• tente acessar novamente em instantes</li>
                    <li>• peça um novo convite ao responsável, se necessário</li>
                  </ul>
                </div>

                <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-sm font-medium text-neutral-100">
                    Experiência mais humana
                  </div>
                  <p className="mt-2 text-sm leading-6 text-neutral-300">
                    Em vez de parecer falha técnica, a página comunica claramente o
                    estado da pesquisa e mantém a percepção premium do produto.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function EmptySurveyState() {
  return (
    <div className="min-h-screen bg-neutral-950 px-4 py-10 text-neutral-50">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-4xl items-center justify-center">
        <Card className="w-full rounded-[32px] border-white/10 bg-white/[0.04] p-10 shadow-2xl shadow-black/30">
          <div className="max-w-2xl">
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.18em] text-neutral-300">
              Questionário indisponível
            </div>

            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-white">
              Nenhuma pergunta disponível
            </h1>

            <p className="mt-4 text-sm leading-7 text-neutral-300">
              Este questionário não possui perguntas publicadas no momento. Tente
              novamente mais tarde ou entre em contato com o organizador da campanha.
            </p>

            <div className="mt-8">
              <a href={NORMEX_WEBSITE} target="_blank" rel="noreferrer">
                <Button
                  variant="outline"
                  className="rounded-2xl border-white/15 bg-white/5 text-neutral-100 hover:bg-white/10 hover:text-white"
                >
                  Ir para o site da Normex
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>();
  const surveyQ = usePublicSurvey(token);
  const submitMut = useSubmitPublicSurvey(token || "");

  const [answers, setAnswers] = useState<AnswersMap>({});
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [supportTextModalOpen, setSupportTextModalOpen] = useState(false);
  const [supportTextNeedsModal, setSupportTextNeedsModal] = useState(false);
  const supportTextPreviewRef = useRef<HTMLDivElement>(null);

  const survey = surveyQ.data;

  const groupedSections = useMemo(() => {
    if (!survey) return [];
    return buildGroupedQuestions(survey.questions, survey.sections);
  }, [survey]);

  const flatQuestions = useMemo(
    () => groupedSections.flatMap((section) => section.questions),
    [groupedSections],
  );

  const currentQuestion = flatQuestions[currentIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;

  const answeredCount = useMemo(() => {
    if (!survey) return 0;
    return survey.questions.filter((q) => questionIsAnswered(q, answers[q.id])).length;
  }, [survey, answers]);

  const progress = flatQuestions.length
    ? Math.round((answeredCount / flatQuestions.length) * 100)
    : 0;

  const requiredMissingCount = useMemo(() => {
    if (!survey) return 0;
    return survey.questions.filter(
      (q) => q.is_required && !questionIsAnswered(q, answers[q.id]),
    ).length;
  }, [survey, answers]);

  const currentSectionIndex = useMemo(() => {
    if (!currentQuestion) return 0;
    return Math.max(
      0,
      groupedSections.findIndex((section) =>
        section.questions.some((q: PublicSurveyQuestion) => q.id === currentQuestion.id),
      ),
    );
  }, [groupedSections, currentQuestion]);

  const dateRangeLabel = formatDateRange(survey?.starts_at, survey?.ends_at);
  const versionLabel = survey?.questionnaire_version_label || survey?.questionnaire_version || "";

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!survey || !currentQuestion) return;
      if (submitMut.isPending) return;

      if (event.key === "ArrowRight" || event.key === "Enter") {
        const tag = (document.activeElement?.tagName || "").toLowerCase();
        if (tag === "textarea" || tag === "input") return;
        event.preventDefault();
        handleNext();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handlePrevious();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [survey, currentQuestion, submitMut.isPending, currentIndex, answers]);
  useEffect(() => {
    setSupportTextModalOpen(false);
  }, [currentQuestion?.id]);

  useEffect(() => {
    const element = supportTextPreviewRef.current;

    if (!element || !currentQuestion?.help_text?.trim()) {
      setSupportTextNeedsModal(false);
      return;
    }

    function measureSupportText() {
      const target = supportTextPreviewRef.current;
      if (!target) return;

      setSupportTextNeedsModal(target.scrollHeight - target.clientHeight > 1);
    }

    const frame = window.requestAnimationFrame(measureSupportText);
    window.addEventListener("resize", measureSupportText);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measureSupportText);
    };
  }, [currentQuestion?.id, currentQuestion?.help_text]);

  useEffect(() => {
    if (!supportTextModalOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSupportTextModalOpen(false);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [supportTextModalOpen]);


  function setOptionAnswer(questionId: string, optionId: string) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        kind: "option",
        selected_option_id: optionId,
      },
    }));
  }

  function setTextAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        kind: "text",
        answer_text: value.slice(0, TEXT_ANSWER_MAX_LENGTH),
      },
    }));
  }

  function toggleMultipleAnswer(questionId: string, optionText: string) {
    setAnswers((prev) => {
      const current = prev[questionId];
      const selected = current?.kind === "multiple" ? current.selected_options : [];
      const exists = selected.includes(optionText);
      const next = exists
        ? selected.filter((item) => item !== optionText)
        : [...selected, optionText];

      return {
        ...prev,
        [questionId]: {
          kind: "multiple",
          selected_options: next,
        },
      };
    });
  }

  function setNumericAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        kind: "numeric",
        answer_numeric: value,
      },
    }));
  }

  function handlePrevious() {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }

  function handleNext() {
    setCurrentIndex((prev) => Math.min(flatQuestions.length - 1, prev + 1));
  }

  function goToSection(sectionIndex: number) {
    const targetSection = groupedSections[sectionIndex];
    if (!targetSection || !targetSection.questions.length) return;

    const firstQuestionId = targetSection.questions[0].id;
    const targetIndex = flatQuestions.findIndex((q) => q.id === firstQuestionId);
    if (targetIndex >= 0) {
      setCurrentIndex(targetIndex);
    }
  }

  async function handleSubmit() {
    if (!survey) return;

    if (requiredMissingCount > 0) {
      toast.error(
        `Ainda existem ${requiredMissingCount} pergunta(s) obrigatória(s) pendente(s).`,
      );
      return;
    }

    const invalidMultipleQuestion = survey.questions.find((question) => {
      if (getQuestionKind(question.question_type) !== "multiple") return false;
      const value = answers[question.id];
      const count = value?.kind === "multiple" ? value.selected_options.length : 0;
      const min = Number(question.min_selected_options || 0);
      const max = question.max_selected_options ? Number(question.max_selected_options) : null;
      return (min > 0 && count < min) || (max !== null && count > max);
    });

    if (invalidMultipleQuestion) {
      toast.error("Revise a quantidade de opções selecionadas na pergunta de múltipla escolha.");
      return;
    }

    const payload = {
      answers: buildSubmitAnswers(survey.questions, answers),
    };

    try {
      await submitMut.mutateAsync(payload);
      setSubmitted(true);
      toast.success("Respostas enviadas com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar respostas.");
    }
  }

  if (surveyQ.isLoading) {
    return <SurveySkeleton />;
  }

  if (surveyQ.isError || !survey) {
    const errorDetail = getPublicSurveyErrorDetail(surveyQ.error);

    return (
      <SurveyUnavailableState
        variant={getPublicSurveyUnavailableVariant(errorDetail)}
        detail={errorDetail}
      />
    );
  }

  if (submitted || survey.token_status === "submitted") {
    return <SurveyFinishedState tenantName={survey.tenant_name} />;
  }

  if (!currentQuestion) {
    return <EmptySurveyState />;
  }

  const questionKind = getQuestionKind(currentQuestion.question_type);
  const supportText = currentQuestion.help_text?.trim() || "";
  const currentSection = groupedSections[currentSectionIndex];
  const currentSectionProgress = currentSection
    ? getSectionProgress(currentSection, answers)
    : null;
  const currentQuestionAnswered = questionIsAnswered(currentQuestion, currentAnswer);
  const isLastQuestion = currentIndex === flatQuestions.length - 1;

  return (
    <div className="min-h-[100dvh] bg-neutral-950 text-neutral-50 lg:h-[100dvh] lg:overflow-hidden">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-neutral-950/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">
              {survey.tenant_name}
            </div>
            <div className="mt-1 line-clamp-1 text-sm font-semibold text-white">
              {survey.questionnaire_title}
            </div>
          </div>

          <div className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-neutral-300">
            {currentIndex + 1}/{flatQuestions.length}
          </div>
        </div>

        <div className="mt-3 h-1.5 rounded-full bg-white/10">
          <div
            className="h-1.5 rounded-full bg-white transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="mx-auto grid min-h-[calc(100dvh-76px)] max-w-7xl grid-cols-1 lg:h-[100dvh] lg:min-h-0 lg:grid-cols-[400px_minmax(0,1fr)]">
        <aside className="hidden border-r border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.15),_transparent_35%),linear-gradient(180deg,_rgba(255,255,255,0.03),_rgba(255,255,255,0.01))] p-6 lg:block lg:h-[100dvh] lg:min-h-0 lg:overflow-y-auto lg:p-8">
          <div className="flex h-full flex-col">
            <div className="space-y-5">
              <Card className="rounded-[28px] border-white/10 bg-white/[0.04] p-4 shadow-2xl shadow-black/20">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-neutral-900">
                    <Building2 className="h-6 w-6" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">
                      Empresa
                    </div>
                    <div className="mt-1 break-words text-2xl font-semibold tracking-tight text-white">
                      {survey.tenant_name}
                    </div>
                    <div className="mt-1 text-sm text-neutral-400">
                      Ambiente seguro e confidencial
                    </div>
                  </div>
                </div>
              </Card>

              <div className="inline-flex w-fit items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm text-neutral-200">
                <Sparkles className="mr-2 h-4 w-4 text-amber-300" />
                Pesquisa psicossocial
              </div>

              <div>
                <h1 className="text-4xl font-semibold tracking-tight text-white">
                  {survey.questionnaire_title}
                </h1>

                {survey.questionnaire_description ? (
                  <p className="mt-4 text-base leading-8 text-neutral-300">
                    {survey.questionnaire_description}
                  </p>
                ) : null}

                {survey.questionnaire_instructions ? (
                  <p className="mt-3 text-sm leading-7 text-neutral-400">
                    {survey.questionnaire_instructions}
                  </p>
                ) : null}
              </div>

              <Card className="rounded-[28px] border-white/10 bg-white/[0.04] p-4">
                <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">
                  Progresso
                </div>

                <div className="mt-3 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-5xl font-semibold tracking-tight text-white">
                      {progress}%
                    </div>
                    <div className="mt-1 text-sm text-neutral-400">
                      {answeredCount} de {flatQuestions.length} respondidas
                    </div>
                  </div>

                  <div className="whitespace-nowrap text-sm text-neutral-400">
                    Pergunta {currentIndex + 1} / {flatQuestions.length}
                  </div>
                </div>

                <div className="mt-4 h-2 rounded-full bg-white/10">
                  <div
                    className="h-2 rounded-full bg-white transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </Card>

              <Card className="rounded-[28px] border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center gap-2 text-white">
                  <LockKeyhole className="h-4 w-4" />
                  <span className="font-medium">Privacidade protegida</span>
                </div>
                <p className="mt-3 text-sm leading-7 text-neutral-400">
                  As respostas são tratadas conforme o modo de anonimização da campanha
                  e as regras de governança da plataforma.
                </p>
              </Card>

              <div>
                <div className="mb-4 text-xs uppercase tracking-[0.3em] text-neutral-500">
                  Etapas
                </div>

                <div className="space-y-3">
                  {groupedSections.map((section, sectionIndex) => {
                    const isActive = sectionIndex === currentSectionIndex;
                    const sectionProgress = getSectionProgress(section, answers);

                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => goToSection(sectionIndex)}
                        className={`w-full rounded-[22px] border px-4 py-4 text-left transition-all ${
                          isActive
                            ? "border-white/20 bg-white/[0.08] shadow-lg shadow-black/20"
                            : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-lg font-medium text-white">{section.title}</div>
                            {section.description ? (
                              <div className="mt-2 text-sm leading-6 text-neutral-400">
                                {section.description}
                              </div>
                            ) : null}
                          </div>

                          <div className="shrink-0 text-sm text-neutral-300">
                            {sectionProgress.answered}/{sectionProgress.total}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-2 pt-2">
              {versionLabel ? (
                <div className="text-xs text-neutral-500">
                  Versão do questionário: {versionLabel}
                </div>
              ) : null}

              <a
                href={NORMEX_WEBSITE}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-xs text-neutral-500 transition-colors hover:text-neutral-300"
              >
                Developed by Normex®
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </aside>

        <main className="flex min-h-[calc(100dvh-76px)] flex-col px-3 py-3 sm:px-4 sm:py-4 lg:h-[100dvh] lg:min-h-0 lg:overflow-hidden lg:p-6">
          <div className="hidden shrink-0 flex-col gap-4 pb-3 lg:flex xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">
                Demanda
              </div>
              <div className="mt-2 text-lg text-white">{dateRangeLabel}</div>
            </div>

            <div className="inline-flex w-fit items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-300">
              {requiredMissingCount} obrigatória(s) pendente(s)
            </div>
          </div>

          <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[26px] border-white/10 bg-white/[0.03] shadow-2xl shadow-black/25 lg:rounded-[32px]">
            <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-5 lg:p-6">
              <div className="mb-3 flex items-start justify-between gap-3 lg:mb-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-medium uppercase tracking-[0.24em] text-neutral-500 lg:text-xs lg:tracking-[0.3em]">
                    Pergunta {currentIndex + 1}
                  </div>

                  {currentSection ? (
                    <div className="mt-1 line-clamp-1 text-xs text-neutral-400 lg:hidden">
                      {currentSection.title}
                      {currentSectionProgress ? (
                        <span> · {currentSectionProgress.answered}/{currentSectionProgress.total}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${
                    currentQuestionAnswered
                      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
                      : currentQuestion.is_required
                        ? "border-amber-400/20 bg-amber-400/10 text-amber-300"
                        : "border-white/10 bg-white/[0.04] text-neutral-300"
                  }`}
                >
                  {currentQuestionAnswered
                    ? "Respondida"
                    : currentQuestion.is_required
                      ? "Obrigatória"
                      : "Opcional"}
                </div>
              </div>

              <div className="text-xl font-semibold leading-snug tracking-tight text-white sm:text-2xl lg:max-w-4xl lg:text-3xl lg:leading-tight">
                {currentQuestion.question_text}
              </div>

              {supportText ? (
                <div className="mt-2 lg:mt-4 lg:max-w-3xl">
                  <div
                    ref={supportTextPreviewRef}
                    className="line-clamp-3 text-xs leading-5 text-neutral-400 sm:text-sm lg:leading-7"
                  >
                    {supportText}
                  </div>

                  {supportTextNeedsModal ? (
                    <button
                      type="button"
                      onClick={() => setSupportTextModalOpen(true)}
                      className="mt-1 text-xs font-semibold text-violet-300 transition-colors hover:text-violet-200"
                    >
                      Ler mais
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 lg:mt-5 lg:space-y-3 lg:pr-2">
                {questionKind === "choice" ? (
                  currentQuestion.options.map((option, optionIndex) => {
                    const isSelected =
                      currentAnswer?.kind === "option" &&
                      currentAnswer.selected_option_id === option.id;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setOptionAnswer(currentQuestion.id, option.id)}
                        className={`flex w-full items-center gap-3 rounded-[18px] border px-4 py-3 text-left transition-all sm:py-3.5 lg:gap-4 lg:rounded-[22px] lg:px-5 lg:py-3.5 ${
                          isSelected
                            ? "border-white/30 bg-white/[0.08] shadow-lg shadow-black/20"
                            : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-semibold lg:h-10 lg:w-10 lg:rounded-2xl lg:text-sm ${
                            isSelected
                              ? "bg-white text-neutral-900"
                              : "bg-white/10 text-neutral-200"
                          }`}
                        >
                          {optionIndex + 1}
                        </div>

                        <div className="text-base font-medium leading-6 text-white sm:text-lg lg:text-xl lg:leading-7">
                          {option.option_text}
                        </div>
                      </button>
                    );
                  })
                ) : null}

                {questionKind === "system_select" ? (
                  <select
                    value={currentAnswer?.kind === "text" ? currentAnswer.answer_text : ""}
                    onChange={(e) => setTextAnswer(currentQuestion.id, e.target.value)}
                    className="h-12 w-full rounded-[18px] border border-white/10 bg-neutral-900 px-4 text-base text-white outline-none focus:border-white/25 lg:h-14 lg:rounded-[22px] lg:px-5"
                  >
                    <option value="">Selecione uma opção</option>
                    {currentQuestion.options.map((option) => (
                      <option key={option.id} value={option.option_text}>
                        {option.option_text}
                      </option>
                    ))}
                  </select>
                ) : null}

                {questionKind === "multiple" ? (
                  <>
                    <div className="rounded-[18px] border border-white/10 bg-white/[0.03] px-4 py-3 text-xs leading-5 text-neutral-300 lg:rounded-[22px] lg:px-5 lg:py-4 lg:text-sm">
                      {currentQuestion.min_selected_options ? (
                        <span>Selecione pelo menos {currentQuestion.min_selected_options} opção(ões). </span>
                      ) : null}
                      {currentQuestion.max_selected_options ? (
                        <span>Selecione no máximo {currentQuestion.max_selected_options} opção(ões).</span>
                      ) : null}
                    </div>
                    {currentQuestion.options.map((option, optionIndex) => {
                      const selected =
                        currentAnswer?.kind === "multiple"
                          ? currentAnswer.selected_options
                          : [];
                      const isSelected = selected.includes(option.option_text);
                      const maxReached =
                        !!currentQuestion.max_selected_options &&
                        selected.length >= currentQuestion.max_selected_options &&
                        !isSelected;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            if (maxReached) {
                              toast.error(`Selecione no máximo ${currentQuestion.max_selected_options} opção(ões).`);
                              return;
                            }
                            toggleMultipleAnswer(currentQuestion.id, option.option_text);
                          }}
                          className={`flex w-full items-center gap-3 rounded-[18px] border px-4 py-3 text-left transition-all sm:py-3.5 lg:gap-4 lg:rounded-[22px] lg:px-5 lg:py-3.5 ${
                            isSelected
                              ? "border-white/30 bg-white/[0.08] shadow-lg shadow-black/20"
                              : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"
                          }`}
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-semibold lg:h-10 lg:w-10 lg:rounded-2xl lg:text-sm ${
                              isSelected
                                ? "bg-white text-neutral-900"
                                : "bg-white/10 text-neutral-200"
                            }`}
                          >
                            {isSelected ? "✓" : optionIndex + 1}
                          </div>

                          <div className="text-base font-medium leading-6 text-white sm:text-lg lg:text-xl lg:leading-7">
                            {option.option_text}
                          </div>
                        </button>
                      );
                    })}
                  </>
                ) : null}

                {questionKind === "text" ? (
                  <div>
                    <textarea
                      value={currentAnswer?.kind === "text" ? currentAnswer.answer_text : ""}
                      onChange={(e) => setTextAnswer(currentQuestion.id, e.target.value)}
                      maxLength={TEXT_ANSWER_MAX_LENGTH}
                      placeholder="Digite sua resposta..."
                      className="min-h-[132px] w-full rounded-[18px] border border-white/10 bg-white/[0.03] px-4 py-3 text-base text-white outline-none placeholder:text-neutral-500 focus:border-white/25 lg:min-h-[180px] lg:rounded-[22px] lg:px-5 lg:py-4"
                    />
                    <div className="mt-2 text-right text-xs text-neutral-500">
                      {currentAnswer?.kind === "text" ? currentAnswer.answer_text.length : 0}/{TEXT_ANSWER_MAX_LENGTH}
                    </div>
                  </div>
                ) : null}

                {questionKind === "numeric" ? (
                  <input
                    type="number"
                    value={
                      currentAnswer?.kind === "numeric" ? currentAnswer.answer_numeric : ""
                    }
                    onChange={(e) => setNumericAnswer(currentQuestion.id, e.target.value)}
                    step={1}
                    placeholder="Digite um número inteiro"
                    className="h-12 w-full rounded-[18px] border border-white/10 bg-white/[0.03] px-4 text-base text-white outline-none placeholder:text-neutral-500 focus:border-white/25 lg:h-14 lg:rounded-[22px] lg:px-5"
                  />
                ) : null}
              </div>
            </div>

            <div className="shrink-0 border-t border-white/10 bg-neutral-950/70 p-3 sm:p-4 lg:bg-neutral-950/40 lg:p-4">
              <div className="flex items-center gap-3 sm:justify-between lg:gap-4">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0 || submitMut.isPending}
                  className="h-11 flex-1 rounded-2xl border-white/15 bg-transparent text-white hover:bg-white/5 sm:flex-none lg:h-12 lg:min-w-[170px] lg:rounded-full lg:bg-white/[0.04] lg:px-6 lg:text-sm lg:font-semibold lg:text-neutral-200 lg:hover:bg-white/[0.08]"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Anterior
                </Button>

                {isLastQuestion ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitMut.isPending}
                    className="h-11 flex-1 rounded-2xl sm:flex-none lg:h-12 lg:min-w-[180px] lg:rounded-full lg:bg-violet-600 lg:px-7 lg:text-sm lg:font-semibold lg:text-white lg:shadow-lg lg:shadow-violet-950/40 lg:hover:bg-violet-500"
                  >
                    {submitMut.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Enviar
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={submitMut.isPending}
                    className="h-11 flex-1 rounded-2xl sm:flex-none lg:h-12 lg:min-w-[180px] lg:rounded-full lg:bg-violet-600 lg:px-7 lg:text-sm lg:font-semibold lg:text-white lg:shadow-lg lg:shadow-violet-950/40 lg:hover:bg-violet-500"
                  >
                    Próxima
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="mt-3 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-600">
                powered by Normex
              </div>
            </div>
          </Card>
        </main>
      </div>

      {supportTextModalOpen && supportText ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="support-text-modal-title"
        >
          <div className="flex max-h-[82dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] border border-white/10 bg-neutral-950 shadow-2xl shadow-black/40 sm:rounded-[28px]">
            <div className="shrink-0 border-b border-white/10 px-5 py-4">
              <div
                id="support-text-modal-title"
                className="text-sm font-semibold text-white"
              >
                Texto de apoio
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <p className="whitespace-pre-line text-sm leading-7 text-neutral-300">
                {supportText}
              </p>
            </div>

            <div className="shrink-0 border-t border-white/10 bg-neutral-950 px-5 py-4">
              <Button
                type="button"
                onClick={() => setSupportTextModalOpen(false)}
                className="h-11 w-full rounded-2xl"
              >
                Entendi
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
